/**
 * traciAutoLinker.js
 *
 * Uses Gemini + the TRACI lookup DB to auto-match LCI input names to
 * ecoinvent activities and propose traci_scores_per_unit for each unlinked input.
 *
 * Steps per input:
 *   1. Search TRACI DB with the raw input name (term-based, fast)
 *   2. If top result score is high → auto-accept
 *   3. Pass top candidates to Gemini with context to pick best match + confidence
 *   4. Return proposals for user review
 */

import ipc from './ipc';
import { callGemini } from './geminiService';
import { getActiveImpactSource, hasImpactScores, isCostOnly, setImpactLink, ECOINVENT_SOURCE } from './impactSource';

const CONFIDENCE_LABELS = {
  high: { label: 'High', color: '#2e7d32' },
  medium: { label: 'Medium', color: '#e65100' },
  low: { label: 'Low', color: '#c62828' },
};

export { CONFIDENCE_LABELS };

// Where ecoinvent sends a consumer whose own location has no market.
const FALLBACK_LOCATIONS = ['RoW', 'GLO'];

const sameText = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

/**
 * How well a lookup row fits an input that already names a location. The
 * search matches names only, and one activity can have a row per country (175
 * for medium-voltage electricity), so the first hit is whichever country comes
 * first in the file. Rank what ecoinvent's own linking gives a consumer in the
 * input's location: the same activity there, then the same product's market
 * group there (CN electricity has no plain market since 3.10), then the
 * RoW/GLO market. Lower is better; 4 means the location says nothing.
 */
export function locationRank(row, { activityName, referenceProduct, location }) {
  if (!location) return 4;
  const sameActivity = sameText(row.activity_name, activityName);
  const sameProduct = referenceProduct ? sameText(row.reference_product, referenceProduct) : sameActivity;
  const here = row.location === location;
  const fallback = FALLBACK_LOCATIONS.includes(row.location);
  if (here && sameActivity) return 0;
  if (here && sameProduct) return 1;
  if (fallback && sameActivity) return 2;
  if (fallback && sameProduct) return 3;
  return 4;
}

/**
 * Search the lookup for an input ({ activityName, inputName, referenceProduct,
 * location, impactSource }). When the input names a location, rows there and in
 * RoW/GLO are searched for too, so they are found even when the name alone
 * fills the results with other countries, and the best-placed row comes first.
 * `impactSource` picks the dataset's lookup (ecoinvent by default); results
 * never mix datasets.
 */
export async function searchForInput(inp, maxResults = 10) {
  const query = inp.activityName || inp.inputName;
  const source = inp.impactSource || ECOINVENT_SOURCE;
  const places = inp.location ? [inp.location, ...FALLBACK_LOCATIONS] : [];
  const responses = await Promise.all([
    ...places.map(place => ipc.invoke('search-traci-lookup', `${query} ${place}`, maxResults, undefined, source)),
    ipc.invoke('search-traci-lookup', query, maxResults, undefined, source),
  ]);
  const seen = new Set();
  const rows = [];
  for (const row of responses.flatMap(r => r?.results || [])) {
    const key = `${row.activity_name}|${row.location}|${row.reference_product}`;
    if (!seen.has(key)) {
      seen.add(key);
      rows.push(row);
    }
  }
  // sort is stable, so search order survives within a rank.
  return rows.sort((a, b) => locationRank(a, inp) - locationRank(b, inp)).slice(0, maxResults);
}

/**
 * Collect all unique unlinked inputs from a project.
 * Returns [{ procId, procName, inputIndex, inputName, activityName, referenceProduct, location, unit, quantity }]
 */
export function collectUnlinkedInputs(project) {
  if (!project?.processes) return [];
  const impactSource = getActiveImpactSource(project);
  const seen = new Set();
  const unlinked = [];

  for (const [procId, proc] of Object.entries(project.processes)) {
    for (let i = 0; i < (proc.lci_inputs || []).length; i++) {
      const input = proc.lci_inputs[i];
      if (!isCostOnly(input) && !hasImpactScores(input, impactSource)) {
        // The same activity in two places is two datasets.
        const key = `${input.activity_name || input.name}|${input.location || ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          unlinked.push({
            procId,
            procName: proc.name || procId,
            inputIndex: i,
            inputName: input.name,
            activityName: input.activity_name || input.name,
            referenceProduct: input.reference_product || '',
            location: input.location || '',
            unit: input.unit || 'kg',
            quantity: input.quantity || 1,
            impactSource,
          });
        }
      }
    }
  }
  return unlinked;
}

/**
 * Run auto-linking for a list of unlinked inputs.
 * Returns proposals: [{ inputName, activityName, unit, candidates, bestMatch, confidence, reasoning }]
 *
 * useGemini: if true, pass top candidates through Gemini for smarter ranking.
 */
export async function autoLinkInputs(unlinkedInputs, useGemini = true, onProgress = null) {
  const proposals = [];

  for (let i = 0; i < unlinkedInputs.length; i++) {
    const inp = unlinkedInputs[i];
    if (onProgress) onProgress(i, unlinkedInputs.length, inp.inputName);

    try {
      // Step 1: Term-based TRACI DB search, best-placed rows first
      const query = inp.activityName || inp.inputName;
      const candidates = await searchForInput(inp, 10);

      let bestMatch = candidates[0] || null;
      let confidence = 'low';
      let reasoning = 'No DB match found';
      const rank = bestMatch ? locationRank(bestMatch, inp) : 4;

      if (rank === 0) {
        confidence = 'high';
        reasoning = `Same dataset as the input, in ${inp.location}`;
      } else if (rank <= 3) {
        // The fallback ecoinvent itself uses; worth a look before accepting.
        confidence = 'medium';
        reasoning = `No "${query}" for ${inp.location}; using ${bestMatch.activity_name} | ${bestMatch.location}, as ecoinvent links it`;
      } else if (candidates.length > 0) {
        // Simple heuristic: if first result name closely matches input name → high confidence
        const topName = (candidates[0].reference_product || candidates[0].activity_name || '').toLowerCase();
        const inputLower = query.toLowerCase();
        const overlap = topName.split(' ').filter(w => w.length > 3 && inputLower.includes(w)).length;
        const wordCount = topName.split(' ').length;
        const ratio = wordCount > 0 ? overlap / wordCount : 0;

        if (ratio >= 0.6) {
          confidence = 'high';
          reasoning = 'Strong name match in TRACI database';
        } else if (ratio >= 0.3 || candidates.length >= 3) {
          confidence = 'medium';
          reasoning = 'Partial name match — review recommended';
        } else {
          confidence = 'low';
          reasoning = 'Weak match — Gemini disambiguation used';
        }

        // Step 2: Use Gemini to rank if confidence is not high and useGemini is true
        if (useGemini && confidence !== 'high' && candidates.length > 1) {
          try {
            const candidateList = candidates.slice(0, 5).map((c, idx) =>
              `${idx + 1}. "${c.activity_name}" | product: "${c.reference_product}" | location: ${c.location}`
            ).join('\n');

            const prompt = `You are helping match a process inventory input to an ecoinvent database activity.

Input name: "${query}"
Input location: ${inp.location || 'not given'}
Unit: ${inp.unit}
Process context: ${inp.procName}

Top database candidates:
${candidateList}

Reply with JSON only (no markdown):
{
  "best_index": <1-based index of best match, or 0 if none fit>,
  "confidence": "high" | "medium" | "low",
  "reasoning": "<one sentence>"
}`;

            const responseText = await callGemini([{ role: 'user', text: prompt }]);
            let clean = responseText.trim();
            if (clean.startsWith('```')) clean = clean.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
            const parsed = JSON.parse(clean);

            const idx = (parsed.best_index || 1) - 1;
            if (idx >= 0 && idx < candidates.length) {
              bestMatch = candidates[idx];
              confidence = parsed.confidence || confidence;
              reasoning = parsed.reasoning || reasoning;
            }
          } catch (_) {
            // Gemini failed — keep heuristic result
          }
        }

        // A name match from another country is never auto-accepted.
        if (inp.location && bestMatch && bestMatch.location !== inp.location && confidence === 'high') {
          confidence = 'medium';
          reasoning = `Name matches, but the dataset is for ${bestMatch.location}, not ${inp.location}`;
        }
      }

      proposals.push({
        inputName: inp.inputName,
        activityName: inp.activityName,
        location: inp.location,
        unit: inp.unit,
        procId: inp.procId,
        procName: inp.procName,
        candidates: candidates.slice(0, 5),
        bestMatch,
        confidence,
        reasoning,
        accepted: confidence === 'high', // auto-accept high confidence
      });
    } catch (err) {
      proposals.push({
        inputName: inp.inputName,
        activityName: inp.activityName,
        location: inp.location,
        unit: inp.unit,
        procId: inp.procId,
        procName: inp.procName,
        candidates: [],
        bestMatch: null,
        confidence: 'low',
        reasoning: `Error: ${err.message}`,
        accepted: false,
      });
    }
  }

  return proposals;
}

/**
 * Apply accepted proposals to the project.
 * Returns a new project object with traci_scores_per_unit written to matched inputs.
 */
export function applyProposals(project, proposals) {
  const updated = JSON.parse(JSON.stringify(project)); // deep clone
  const impactSource = getActiveImpactSource(updated);

  for (const proposal of proposals) {
    if (!proposal.accepted || !proposal.bestMatch) continue;
    const match = proposal.bestMatch;

    // collectUnlinkedInputs proposes once per activity and location, so the
    // link goes to every unlinked input that shares them, in any process.
    for (const proc of Object.values(updated.processes || {})) {
      const inputs = proc.lci_inputs || [];
      for (let i = 0; i < inputs.length; i++) {
        const input = inputs[i];
        const name = input.activity_name || input.name;
        if (name !== proposal.activityName) continue;
        if ((input.location || '') !== (proposal.location || '')) continue;
        if (hasImpactScores(input, impactSource)) continue;
        // The labels name the dataset the scores came from, not the one the
        // input asked for: a CN input matched to RoW must say RoW.
        inputs[i] = setImpactLink(input, impactSource, {
          scores: match.traci_scores,
          activity_name: match.activity_name,
          reference_product: match.reference_product,
          location: match.location,
        });
      }
    }
  }

  return updated;
}
